import { memo, useState } from "react";
import SafetyScoreRing from "../../shared/SafetyScoreRing";
import StatCardRow from "../../shared/StatCardRow";
import HBarList from "../../shared/HBarList";
import RecordModal from "../../shared/RecordModal";
import DonutRing from "../../shared/DonutRing";
import TableLoading from "../../shared/TableLoading";
import { usePaginatedList } from "../../hooks/usePaginatedList";
import { useMountFlash } from "../../hooks/useMountFlash";
import { toPlainText } from "../../utils/plainText";
import { fetchCases } from "../../services/dashboardApi";
import { formatDate, formatNum } from "../../utils/formatters";
import "../../reportDashboard.scss";

const pct = (part, total) => (total > 0 ? `${Math.round((part / total) * 100)}%` : "0%");

const OverviewSection = ({ data, apiCtx, refetching }) => {
  const { safetyScore, kpis, hotspots = [], topCategories = [] } = data;
  const reporterAccountability = data.reporterAccountability || [];
  const learningManagement = data.learningManagement || {
    lessonCourseAssigned: 0,
    lessonCourseCompleted: 0,
    meetingInstructorsAssigned: 0,
    meetingInstructorsCompleted: 0,
  };
  const [reporterDetail, setReporterDetail] = useState(null);

  // Page-payload-fed tables below have no fetch of their own to report; this
  // gives them a brief loading state when the section mounts (tab switch) and
  // the real one whenever the page payload is refetching.
  const mountFlash = useMountFlash();
  const tableLoading = refetching || mountFlash;

  // "corrective" is the all-time-in-range count (any status), sourced from
  // ReportLogDetail — used only where a card is explicitly captioned "total"
  // (Open Corrective's subtitle, Top Corrective Categories). Action/Reprimand
  // have no such "total" caption anywhere in this section, so only their
  // Open variants below are needed.
  const kudos = kpis.kudosItems;
  const corrective = kpis.correctiveItemsTotal;

  // Open-only counterparts — these are what Item Management's default Open
  // filter (statusIds=2 on GetItemList) shows, verified against live QA data:
  // openCorrectiveItemsTotal/openActionItems/openReprimandItemsTotal matched
  // Item Management's Corrective/Action/Reprimand counts exactly, while the
  // *Total fields above (all-time, any status) did not. Kudos has no open
  // state (backend never counts it toward totalOpenItems), so it's excluded
  // below rather than given an "open" variant.
  const openCorrective = kpis.openCorrectiveItemsTotal;
  const openAction = kpis.openActionItems;
  const openReprimand = kpis.openReprimandItemsTotal;

  // Current-backlog total: Kudos (total, no open state) + the three Open-only
  // counts above. This is what "Total Items", the donut ring, and the Safety
  // Score's Safety Culture dimension all sum to now — the backend now owns
  // this number (kpis.backlogTotalItems) so every surface reconciles against
  // one source instead of each recomputing it locally. kpis.totalItems (410)
  // is a *different*, all-time count (any status) and would leave the
  // ring/bar under-filled with a stray grey sector if used here instead.
  const backlogTotal = kpis.backlogTotalItems;

  const statItems = [
    {
      label: "Total Items",
      value: backlogTotal,
      tone: "primary",
      subtitle: `${kudos} kudos · ${openCorrective} corrective`,
    },
    {
      label: "Kudos",
      value: kudos,
      tone: "kudos",
      subtitle: `${pct(kudos, backlogTotal)} of total`,
    },
    {
      label: "Open Corrective",
      value: openCorrective,
      tone: "corrective",
      subtitle: `${corrective} total corrective`,
    },
    {
      label: "Open Cases",
      value: kpis.openCases,
      tone: "danger",
      subtitle: `${kpis.totalCases} total cases`,
      badge: kpis.openCases > 0 ? `${kpis.openCases} OPEN` : null,
    },
    {
      label: "Active Projects",
      value: kpis.activeProjects,
      tone: "primary",
      subtitle: `${kpis.totalProjects} total projects`,
    },
    {
      label: "Credential Alerts",
      value: kpis.expiredCredentials + kpis.criticalCredentials + kpis.highCredentials,
      tone: "danger",
      subtitle: `${kpis.expiredCredentials} expired · ${kpis.criticalCredentials} critical · ${kpis.highCredentials} high`,
      badge:
        kpis.expiredCredentials + kpis.criticalCredentials > 0 ? "URGENT" : null,
    },
  ];

  // Finding Type Distribution — current backlog, same Open-only framing as
  // the "Open Items" bar below and the same total as the "Total Items" card:
  // Corrective/Action/Reprimand are Open-only, Kudos stays at its total (no
  // open state to show instead). Ring total is backlogTotal, not
  // kpis.totalItems (410, an all-time count) — that mismatch is what
  // previously left a grey unfilled sector and a stale center number.
  const donutTypes = [
    { label: "Kudos", value: kudos, tone: "kudos" },
    { label: "Corrective", value: openCorrective, tone: "corrective" },
    { label: "Action", value: openAction, tone: "action" },
    { label: "Reprimand", value: openReprimand, tone: "reprimand" },
  ].filter((t) => t.value > 0);

  // "Open Items" stacked bar — same four segments and same backlogTotal
  // denominator as the donut above, matching the HTML mockup exactly (v26
  // lines 1068-1071: Kudos is the bar's first segment, denominator is
  // oi.length — the full item set, not an open-only subset). Kudos has no
  // open *status*, but it is still counted here because the mockup counts it
  // here; do not exclude it from this visual.
  const stackedSegments = [
    { label: "Kudos", value: kudos, tone: "kudos" },
    { label: "Corrective", value: openCorrective, tone: "corrective" },
    { label: "Action", value: openAction, tone: "action" },
    { label: "Reprimand", value: openReprimand, tone: "reprimand" },
  ].filter((s) => s.value > 0);

  // Follows the HTML mockup's count>0 behaviour (v26 lines 630-637): a bucket
  // that's currently at zero doesn't get a card, and the card falls back to a
  // single success line when all three are zero. Labels still drop the
  // "(≤30d)"/"(≤90d)" day-threshold suffix (those thresholds are defined in
  // Credentials & Training) so they don't wrap inside this card's narrow row.
  const credAlerts = [
    { label: "Expired", count: kpis.expiredCredentials },
    { label: "Critical", count: kpis.criticalCredentials },
    { label: "High", count: kpis.highCredentials },
  ].filter((a) => a.count > 0);

  const credentialPortfolio = [
    { label: "Active", value: kpis.lowRiskCredentials, tone: "kudos" },
    { label: "Expired", value: kpis.expiredCredentials, tone: "danger" },
    {
      label: "Critical / High",
      value: kpis.criticalCredentials + kpis.highCredentials,
      tone: "warning",
    },
    { label: "No Expiry Set", value: kpis.noDateCredentials, tone: "muted" },
  ];

  const topReporters = [...reporterAccountability]
    .sort((a, b) => b.total - a.total)
    .slice(0, 10);

  // Same server-paged treatment as IncidentsSection's reporter modal (T5/T6
  // shared the identical one-shot pageSize:200 bug): keyed on the selected
  // reporter, with the CredentialsSection.assignmentList guard pattern —
  // fetchPage resolves an empty page when nothing is selected — so the hook
  // stays unconditional instead of only mounting once a reporter is clicked.
  const reporterCaseList = usePaginatedList(
    (params) =>
      reporterDetail
        ? fetchCases(apiCtx, { search: reporterDetail.reporter, ...params })
        : Promise.resolve({ rows: [], totalCount: 0 }),
    [reporterDetail?.reporter, apiCtx.orgId, apiCtx.token, apiCtx.from, apiCtx.to],
  );
  const openReporterDetail = (r) => setReporterDetail(r);

  return (
    <div>
      {/* SafetyScoreRing renders its own tinted banner — eyebrow, headline,
          dims line and cap note — so it must not be wrapped in an rdCard or
          given a "Safety Score" title here. */}
      <SafetyScoreRing
        score={safetyScore.score}
        grade={safetyScore.grade}
        components={safetyScore.components}
        capNote={safetyScore.capNote}
        activeDimensions={safetyScore.activeDimensions}
        totalDimensions={safetyScore.totalDimensions}
        isScorable={safetyScore.isScorable}
        unscorableNote={safetyScore.unscorableNote}
      />

      <div style={{ marginTop: 18 }}>
        <StatCardRow items={statItems} columns={6} />
      </div>

      <div className="rdGrid rdGrid--3">
        <div className="rdCard rdCard--flush">
          <div className="rdCard__title">
            <span>Top Corrective Categories</span>
            <span className="rdCard__note">{corrective} total</span>
          </div>
          <div className="rdCard__hint">
            By Category — see Inspections for the Sub-Category breakdown
          </div>
          <HBarList
            items={topCategories
              .slice(0, 10)
              .map((c) => ({ label: c.category, value: c.count }))}
            tone="corrective"
            emptyLabel="No corrective items"
          />
        </div>

        <div className="rdCard rdCard--flush rdCard--column">
          <div className="rdCard__title">Finding Type Distribution</div>
          <div className="rdCard__body">
            {donutTypes.length > 0 ? (
              <DonutRing total={backlogTotal} segments={donutTypes} centerCaption="ITEMS" />
            ) : (
              <div className="rdEmpty">No findings recorded</div>
            )}
          </div>
          {backlogTotal > 0 && (
            <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid var(--rd-border)" }}>
              <div className="rdCard__label">Open Items</div>
              <div className="rdBar rdBar--tall">
                {stackedSegments.map((s) => {
                  const share = (s.value / backlogTotal) * 100;
                  return (
                    <div
                      key={s.label}
                      className={`rdBar__fill rdBar__caption is-${s.tone}`}
                      style={{ "--rd-fill": `${share}%` }}
                      title={`${s.label}: ${formatNum(s.value)}`}
                    >
                      {/* Count is printed inside the segment (v26 prints it too),
                          but only where the segment is wide enough to hold it —
                          a share threshold rather than v26's per-type absolute
                          count, since width is what decides whether it fits. */}
                      {share >= 6 ? formatNum(s.value) : ""}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="rdCard rdCard--flush">
          <div className="rdCard__title">Credential Alerts</div>
          <div className="rdCard__hint">
            Reflects current data — not affected by the date range filter above
          </div>
          {credAlerts.length > 0 ? (
            <StatCardRow
              items={credAlerts.map((a) => ({
                label: a.label,
                value: a.count,
                tone: "danger",
              }))}
              columns={credAlerts.length}
              inner
            />
          ) : (
            <div className="rdEmpty rdEmpty--success">
              ✓ No critical credential alerts
            </div>
          )}
          <div className="rdCard__label">Credential Portfolio</div>
          {credentialPortfolio.map((row) => (
            <div className="rdKeyRow" key={row.label}>
              <span className="rdKeyRow__label">
                <span className={`rdDot is-${row.tone}`} />
                {row.label}
              </span>
              <span className={`rdKeyRow__value is-${row.tone}`}>
                {row.value}
              </span>
            </div>
          ))}
          {/* Tracker 3.5. The dashboard stopped excluding these rows from the Expired count
              (D5 -- the card must match Credential Management exactly), but the renewal
              context they used to convey by being silently dropped shouldn't just disappear.
              Reusing rdCard__hint (already used for the "not affected by the date range"
              line above) keeps this at the same small/muted weight instead of introducing a
              new style. Hidden entirely at 0 rather than shown as a permanent "0 have a
              renewal" line -- nothing to say when there's nothing to report. */}
          {kpis.expiredSupersededByRenewal > 0 && (
            <div className="rdCard__hint" style={{ marginTop: 8 }}>
              {kpis.expiredSupersededByRenewal} expired credential
              {kpis.expiredSupersededByRenewal === 1 ? "" : "s"} — of the count above —
              {kpis.expiredSupersededByRenewal === 1 ? " has" : " have"} a renewal on file
              for the same person.
            </div>
          )}
        </div>
      </div>

      <div className="rdGrid rdGrid--2">
        <div className="rdCard rdCard--flush">
          <div className="rdCard__title">
            <span>Hotspot Projects</span>
            <span className="rdCard__note">by corrective count</span>
          </div>
          <HBarList
            items={hotspots
              .slice(0, 8)
              .map((h) => ({ label: h.category, value: h.count }))}
            tone="corrective"
            emptyLabel="No data"
            // v26 hard-caps this label at 180px (HTML:897) because project
            // names run long enough to crowd the corrective count otherwise.
            labelMaxWidth={180}
          />
        </div>

        <div className="rdCard rdCard--flush">
          <div className="rdCard__title">Top Case Reporters</div>
          {tableLoading ? (
            <TableLoading />
          ) : topReporters.length > 0 ? (
            <div className="rdTableWrap">
              <table className="rdTable">
                <thead>
                  <tr>
                    <th>Reporter</th>
                    <th className="rdTable__cell--right">Cases</th>
                  </tr>
                </thead>
                <tbody>
                  {topReporters.map((r) => (
                    <tr
                      key={r.reporter}
                      className="rdTable__row--clickable"
                      onClick={() => openReporterDetail(r)}
                    >
                      <td>{toPlainText(r.reporter)}</td>
                      <td className="rdTable__cell--right rdTable__cell--strong">
                        {r.total}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="rdEmpty">No case data</div>
          )}
        </div>
      </div>

      <div className="rdCard">
        <div className="rdCard__title">Training &amp; Safety Meeting Completion</div>
        <div className="rdCard__hint">
          Feeds the Safety Score&apos;s Training &amp; Meeting Completion
          dimension (15 pts) — lesson/course assignments and safety meetings
          in the selected date range. See Credentials &amp; Training for full
          detail.
        </div>
        <StatCardRow
          items={[
            {
              label: "Lesson/Course Records",
              value: pct(learningManagement.lessonCourseCompleted, learningManagement.lessonCourseAssigned),
              tone: learningManagement.lessonCourseAssigned - learningManagement.lessonCourseCompleted > 0 ? "warning" : "kudos",
              subtitle: `${learningManagement.lessonCourseCompleted} of ${learningManagement.lessonCourseAssigned} completed${learningManagement.lessonCourseAssigned - learningManagement.lessonCourseCompleted > 0 ? ` · ${learningManagement.lessonCourseAssigned - learningManagement.lessonCourseCompleted} outstanding` : ""}`,
            },
            {
              label: "Safety Meeting Completion (Instructor)",
              value: pct(learningManagement.meetingInstructorsCompleted, learningManagement.meetingInstructorsAssigned),
              tone: learningManagement.meetingInstructorsAssigned - learningManagement.meetingInstructorsCompleted > 0 ? "warning" : "kudos",
              subtitle: `${learningManagement.meetingInstructorsCompleted} of ${learningManagement.meetingInstructorsAssigned} completed${learningManagement.meetingInstructorsAssigned - learningManagement.meetingInstructorsCompleted > 0 ? ` · ${learningManagement.meetingInstructorsAssigned - learningManagement.meetingInstructorsCompleted} outstanding` : ""}`,
            },
          ]}
          columns={2}
        />
      </div>

      {reporterDetail && (
        <RecordModal
          open
          onClose={() => setReporterDetail(null)}
          title={`${reporterDetail.reporter} — Case Portfolio`}
          fields={[
            { label: "Total Cases", value: reporterDetail.total },
            { label: "Open Cases", value: reporterDetail.open },
            { label: "Oldest Open", value: `${reporterDetail.maxAge} days` },
          ]}
          warning={
            reporterDetail.open > 0
              ? {
                  tone: reporterDetail.open > 5 ? "danger" : "warning",
                  text: `⚠ ${reporterDetail.open} Open Case${reporterDetail.open > 1 ? "s" : ""} — These case${reporterDetail.open > 1 ? "s are" : " is"} still in Open status — general details have been recorded but no incident forms have been started yet.${reporterDetail.maxAge >= 180 ? ` The oldest has been sitting Open for ${reporterDetail.maxAge} days — well beyond the recommended 30-day window to begin incident documentation.` : ""}`,
                }
              : null
          }
          sections={[
            {
              type: "cards",
              title: "Case History",
              emptyLabel: "No cases found",
              rows: reporterCaseList.rows.map((c) => ({
                title: `Case #${c.caseId}`,
                pill: c.status,
                // Age only means something while a case is still Open — see
                // the same rule in IncidentsSection's reporter modal. Applied
                // inline here since there's no separate `history` array to
                // pre-compute it into anymore.
                meta:
                  (c.status || "").toLowerCase() === "open"
                    ? `${c.ageDays} days old`
                    : undefined,
                note: `${formatDate(c.dateOfEvent) || "—"} · ${c.location || "No location"}`,
              })),
              rowCount: reporterCaseList.totalCount,
              page: reporterCaseList.page,
              pageSize: reporterCaseList.pageSize,
              onPageChange: reporterCaseList.setPage,
              loading: reporterCaseList.loading,
            },
          ]}
        />
      )}
    </div>
  );
};

export default memo(OverviewSection);
